import {makeAutoObservable, observable} from 'mobx';
import {errorMessage} from '../../../errors';
import type {LibraryVoice} from '../../../types';

/** Lists the voices for a narration language and dialect. */
export type ListVoices = (
  query: {language: string; region: string},
  signal: AbortSignal,
) => Promise<{voices?: LibraryVoice[]} | undefined>;

/**
 * The voices the server lists in the narration language, while the voice list shows. A newer
 * request, or closing the list, drops the answer of an earlier one. Voices listed for a language
 * and dialect are kept, so showing the list again doesn't ask for them again.
 */
class LibraryVoicesStore {
  private abort: AbortController | null = null;
  // The language and dialect of the voices listed, or on their way.
  private query: string | null = null;

  /** Null while the voices load. */
  public voices: LibraryVoice[] | null = null;
  public error: string | null = null;

  constructor() {
    makeAutoObservable<LibraryVoicesStore, 'abort' | 'query'>(
      this,
      {abort: false, query: false, voices: observable.ref},
      {autoBind: true},
    );
  }

  /** The voices are on their way. */
  public get loading() {
    return this.voices === null && this.error === null;
  }

  public load(list: ListVoices, query: {language: string; region: string}) {
    const key = `${query.language}-${query.region}`;
    // Already listed; a list that failed, or was stopped on its way, is asked for again.
    if (key === this.query && this.voices !== null && this.error === null)
      return;
    this.stop();
    const controller = new AbortController();
    this.abort = controller;
    this.query = key;
    this.voices = null;
    this.error = null;
    list(query, controller.signal).then(
      result => {
        if (!controller.signal.aborted) this.loaded(result?.voices ?? []);
      },
      error => {
        if (!controller.signal.aborted) this.failed(errorMessage(error));
      },
    );
  }

  public stop() {
    this.abort?.abort();
    this.abort = null;
  }

  public loaded(voices: LibraryVoice[]) {
    this.voices = voices;
  }

  public failed(message: string) {
    this.error = message;
  }

  public reset() {
    this.stop();
    this.query = null;
    this.voices = null;
    this.error = null;
  }
}

export default LibraryVoicesStore;
